(function () { 'use strict';

  /* ============================================================
     COREVIAN UNIVERSITY — EXECUTIVE & OPERATIONS NAVIGATION
     Reads currentRole directly from the app's own global variable.
     No DOM detection. No MutationObserver guessing.
     Backend RLS / RPC authorization remains the security authority.
     ============================================================ */

  const EXECUTIVE_ACCESS = {
    CEO: 'e61d0c96-b6d1-4c09-bead-51c0103044a2',
    COO: 'b9fe8738-610d-4c80-a7b1-167e6e2b6b7d'
  };

  const NAV_ID = 'cvnExecutiveOperationsNavBlock';

  let currentAccess = null;


  /* ============================================================
     SUPABASE CLIENT
     ============================================================ */

  function getUniversityClient() {
    try {
      if (typeof window.getCorevianSupabaseClient !== 'function') return null;
      return window.getCorevianSupabaseClient();
    } catch (e) {
      console.error('[CVN Exec Nav] client error:', e);
      return null;
    }
  }

  async function getCurrentSession() {
    const client = getUniversityClient();
    if (!client) return null;
    const { data, error } = await client.auth.getSession();
    if (error) throw error;
    return data?.session || null;
  }


  /* ============================================================
     ACCESS MAP
     ============================================================ */

  function getAccess(userId) {
    if (userId === EXECUTIVE_ACCESS.CEO) {
      return {
        operatingSystem:     true,
        operationsTracker:   true,
        complianceDocuments: true,
        executiveDemo: true,
        executiveOperationsTrackerDemo: true
      };
    }
    if (userId === EXECUTIVE_ACCESS.COO) {
      return {
        operatingSystem:     false,
        operationsTracker:   true,
        complianceDocuments: true,
        executiveDemo: true,
        executiveOperationsTrackerDemo: true
      };
    }
    return null;
  }


  /* ============================================================
     DEMO MODE — READ DIRECTLY FROM APP GLOBALS

     The app uses:
       window.demoMode    (bool)   — true when demo is active
       window.currentRole (string) — 'employee' | 'trainer' | 'leadership'

     Role map (from the actual code):
       'employee'   → Employee view        → HIDE exec nav
       'trainer'    → Leadership view      → HIDE exec nav
       'leadership' → Executive / Admin    → SHOW O, C, D (never B)

     This is the only source of truth we need. No DOM scanning.
     ============================================================ */

  function isDemoActive() {
    return window.demoMode === true;
  }

  function currentDemoRole() {
    // Returns the app's currentRole string directly
    return typeof window.currentRole === 'string'
      ? window.currentRole
      : null;
  }


  /* ============================================================
     VISIBILITY RESOLUTION
     ============================================================ */

  function resolveVisibleButtons(access) {
    if (!access) return null;

    // Not in demo — show full entitlements
    if (!isDemoActive()) {
      return { ...access };
    }

    // Demo is active — check the role
    const role = currentDemoRole();

    if (role === 'leadership' || role === 'admin') {
      // Executive Leadership or Executive / Administration — show O, C, D
      // Bible (B) never appears in demo
      return {
        operatingSystem:     false,
        operationsTracker:   access.operationsTracker,
        complianceDocuments: access.complianceDocuments,
        executiveDemo:       access.executiveDemo
      };
    }

    // 'employee' or 'trainer' — hide entire section
    return null;
  }


  /* ============================================================
     NAV HTML
     ============================================================ */

  function buildNavButtons(visible) {
    let html = '';

    if (visible.operatingSystem) {
      html += `
        <button class="nav-btn" id="navCorevianOperatingSystem" type="button">
          <div class="num">B</div>
          <div class="nav-text">
            <span class="nav-title">Corevian Operating System</span>
            <span class="nav-sub">Executive governance &amp; institutional knowledge</span>
          </div>
        </button>`;
    }

    if (visible.operationsTracker) {
      html += `
        <button class="nav-btn" id="navOperationsTracker" type="button">
          <div class="num">O</div>
          <div class="nav-text">
            <span class="nav-title">Operations Tracker</span>
            <span class="nav-sub">Dispatch, clients, billing &amp; operational control</span>
          </div>
        </button>`;
    }

    if (visible.complianceDocuments) {
      html += `
        <button class="nav-btn" id="navComplianceDocumentControl" type="button">
          <div class="num">C</div>
          <div class="nav-text">
            <span class="nav-title">Compliance &amp; Document Control</span>
            <span class="nav-sub">Controlled document governance &amp; administration</span>
          </div>
        </button>`;
    }

    if (visible.executiveDemo) {
      html += `
        <button class="nav-btn" id="navExecutiveOperationsDemo" type="button">
          <div class="num">D</div>
          <div class="nav-text">
            <span class="nav-title">Executive Demo</span>
            <span class="nav-sub">Leadership presentation environment</span>
          </div>
        </button>`;
    }
    
if (visible.executiveOperationsTrackerDemo) {
  html += `
    <button class="nav-btn" id="navExecutiveOperationsTrackerDemo" type="button">
      <div class="num">T</div>
      <div class="nav-text">
        <span class="nav-title">Executive Operations Tracker Demo</span>
        <span class="nav-sub">Operations demonstration environment</span>
      </div>
    </button>`;
}

    return html;
  }


  /* ============================================================
     DOM — SINGLE BLOCK, NEVER REMOVED

     The block is inserted once and stays in the DOM permanently.
     Role changes only toggle display and swap button content.
     ============================================================ */

  function ensureBlock() {
    if (document.getElementById(NAV_ID)) return true;

    const sidebar = document.getElementById('sidebar');
    if (!sidebar) return false;

    const block = document.createElement('div');
    block.id        = NAV_ID;
    block.className = 'cvn-executive-operations-nav-block';
    block.innerHTML = `
      <div class="nav-divider"></div>
      <div class="sidebar-section">Executive &amp; Operations</div>
    `;
    sidebar.appendChild(block);

    // Suppress any legacy demo button
    const legacy = document.getElementById('navExecutiveDemo');
    if (legacy) legacy.style.display = 'none';

    return true;
  }

  function applyVisibility(visible) {
    const block = document.getElementById(NAV_ID);
    if (!block) return;

    // Remove current buttons
    block.querySelectorAll('.nav-btn').forEach(b => b.remove());

    if (!visible) {
      // Hide the whole block (only inside demo — never in live session)
      block.style.display = isDemoActive() ? 'none' : '';
      return;
    }

    // Show the block
    block.style.display = '';

    const html = buildNavButtons(visible);
    if (!html.trim()) {
      block.style.display = isDemoActive() ? 'none' : '';
      return;
    }

    block.insertAdjacentHTML('beforeend', html);
    bindEvents();
  }


  /* ============================================================
     INTERCEPT switchDemoRole

     The app calls window.switchDemoRole(role) whenever the user
     clicks a role tab. We wrap it to re-evaluate visibility
     immediately after the role changes.

     This is the reliable hook because we now have the source:
       window.currentRole is set at the top of switchDemoRole()
       before buildNav() and showView() are called.
     ============================================================ */

  function interceptSwitchDemoRole() {
    const original = window.switchDemoRole;
    if (typeof original !== 'function') {
      // App hasn't defined it yet — retry shortly
      setTimeout(interceptSwitchDemoRole, 200);
      return;
    }

    if (original.__cvnWrapped) return; // already wrapped

    window.switchDemoRole = function (role) {
  // Run the demo's normal role switch first
  original.call(this, role);

  // Publish the selected demo role for Executive & Operations visibility
  window.demoMode = true;
  window.currentRole = role;

  // Re-evaluate Executive & Operations immediately
  if (currentAccess) {
    applyVisibility(resolveVisibleButtons(currentAccess));
  }
};

    window.switchDemoRole.__cvnWrapped = true;
  }


  /* ============================================================
     EVENT BINDING
     ============================================================ */

  function clearActive() {
    document.querySelectorAll('#sidebar .nav-btn').forEach(b =>
      b.classList.remove('active')
    );
  }

  function setActive(btn) { clearActive(); if (btn) btn.classList.add('active'); }

  async function openOperatingSystem(btn) {
    if (typeof window.openCorevianOperatingSystem !== 'function') {
      console.error('[CVN Exec Nav] Operating System launcher unavailable.');
      return;
    }
    const ok = await window.openCorevianOperatingSystem();
    if (ok) setActive(btn);
  }

  function openOperationsTracker(btn) {
    if (typeof window.openCorevianOperationsTracker === 'function') {
      setActive(btn);
      window.openCorevianOperationsTracker();
      return;
    }
    console.info('[CVN Exec Nav] Operations Tracker connection pending.');
  }

  function openComplianceDocumentControl(btn) {
    const names = [
      'openCorevianAdminDocuments', 'openAdminDocuments',
      'showAdminDocuments',         'renderAdminDocuments'
    ];
    for (const n of names) {
      if (typeof window[n] === 'function') { setActive(btn); window[n](); return; }
    }
    const legacy = Array.from(
      document.querySelectorAll('button, a, [role="button"]')
    ).find(el => (el.textContent || '').replace(/\s+/g, ' ').trim() === 'Admin Documents');
    if (legacy) { setActive(btn); legacy.click(); return; }
    console.error('[CVN Exec Nav] Compliance & Document Control launcher unavailable.');
  }

  async function openExecutiveDemo(btn) {
    if (typeof window.launchExecutiveDemo !== 'function') {
      console.error('[CVN Exec Nav] Executive Demo launcher unavailable.');
      return;
    }
    setActive(btn);
    await window.launchExecutiveDemo();
  }
    
  
async function openExecutiveOperationsTrackerDemo(btn) {
  if (typeof window.launchExecutiveOperationsTrackerDemo !== 'function') {
    console.error('[Corevian] Executive Operations Tracker v21 launcher unavailable.');
    alert('Executive Operations Tracker demo is not connected yet.');
    return;
  }

  setActive(btn);
  await window.launchExecutiveOperationsTrackerDemo();
}


  function bindEvents() {
    const map = {
      navCorevianOperatingSystem:   btn => openOperatingSystem(btn),
      navOperationsTracker:         btn => openOperationsTracker(btn),
      navComplianceDocumentControl: btn => openComplianceDocumentControl(btn),
      navExecutiveOperationsDemo: btn => openExecutiveDemo(btn),
      navExecutiveOperationsTrackerDemo: btn => openExecutiveOperationsTrackerDemo(btn)

    };
    for (const [id, handler] of Object.entries(map)) {
      const btn = document.getElementById(id);
      if (!btn) continue;
      const fresh = btn.cloneNode(true);
      btn.parentNode.replaceChild(fresh, btn);
      fresh.addEventListener('click', () => handler(fresh));
    }
  }


  /* ============================================================
     INITIALIZATION
     ============================================================ */

  async function initialize() {
    try {
      const session = await getCurrentSession();
      const userId  = session?.user?.id || null;
      const access  = getAccess(userId);

      if (!access) {
        // Not an executive user — ensure block is hidden or absent
        const block = document.getElementById(NAV_ID);
        if (block) block.style.display = 'none';
        currentAccess = null;
        return;
      }

      currentAccess = access;

      // Insert block into sidebar (retries if sidebar not ready yet)
      const ready = ensureBlock();
      if (!ready) {
        setTimeout(initialize, 300);
        return;
      }

      // Apply initial visibility
      applyVisibility(resolveVisibleButtons(access));

      // Wrap switchDemoRole so future role changes update the nav
       window.addEventListener('corevian:demo-role-changed', function(event) {
       if (!currentAccess) return;

  const role = event.detail?.role || null;

  window.demoMode = true;
  window.currentRole = role;

  applyVisibility(resolveVisibleButtons(currentAccess));
});

    } catch (err) {
      console.error('[CVN Exec Nav] Init error:', err);
    }
  }


  /* ============================================================
     AUTH STATE LISTENER
     ============================================================ */

  function installAuthListener() {
    const client = getUniversityClient();
    if (!client) { setTimeout(installAuthListener, 500); return; }

    client.auth.onAuthStateChange(() => {
      currentAccess = null;
      const block = document.getElementById(NAV_ID);
      if (block) block.remove();
      // Re-wrap switchDemoRole on next init
      if (window.switchDemoRole && window.switchDemoRole.__cvnWrapped) {
        delete window.switchDemoRole.__cvnWrapped;
      }
      setTimeout(initialize, 200);
    });
  }


  /* ============================================================
     START
     ============================================================ */

  function start() {
    setTimeout(initialize,      1600);
    setTimeout(installAuthListener, 1800);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

})();